import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

/**
 * Workshop: ContextMenu — lightweight right-click context menu.
 *
 * Built without @radix-ui/react-context-menu (not installed).
 * Matches the visual style of DropdownMenu: dark glass background,
 * rounded corners, subtle border, small text.
 *
 * Usage:
 *   <ContextMenu>
 *     <ContextMenuTrigger>
 *       <div>Right-click me</div>
 *     </ContextMenuTrigger>
 *     <ContextMenuContent>
 *       <ContextMenuItem onClick={handleRename}>Rename</ContextMenuItem>
 *       <ContextMenuSeparator />
 *       <ContextMenuItem destructive onClick={handleDelete}>Delete</ContextMenuItem>
 *     </ContextMenuContent>
 *   </ContextMenu>
 */

/* ---------- Context ---------- */

interface ContextMenuState {
  open: boolean;
  x: number;
  y: number;
  openAt: (x: number, y: number) => void;
  close: () => void;
}

const ContextMenuContext = createContext<ContextMenuState | null>(null);

function useContextMenuContext() {
  const ctx = useContext(ContextMenuContext);
  if (!ctx) {
    throw new Error("ContextMenu sub-components must be used inside <ContextMenu>");
  }
  return ctx;
}

/* ---------- ContextMenu (root) ---------- */

interface ContextMenuProps {
  children: ReactNode;
}

export function ContextMenu({ children }: ContextMenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  function openAt(x: number, y: number) {
    setPosition({ x, y });
    setOpen(true);
  }

  function close() {
    setOpen(false);
  }

  return (
    <ContextMenuContext.Provider
      value={{ open, x: position.x, y: position.y, openAt, close }}
    >
      {children}
    </ContextMenuContext.Provider>
  );
}

/* ---------- ContextMenuTrigger ---------- */

interface ContextMenuTriggerProps {
  children: ReactNode;
  asChild?: boolean;
}

export function ContextMenuTrigger({ children, asChild: _asChild }: ContextMenuTriggerProps) {
  const { openAt } = useContextMenuContext();

  function handleContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    openAt(e.clientX, e.clientY);
  }

  return (
    <div onContextMenu={handleContextMenu} className="contents">
      {children}
    </div>
  );
}

/* ---------- ContextMenuContent ---------- */

interface ContextMenuContentProps {
  children: ReactNode;
  className?: string;
}

export function ContextMenuContent({ children, className }: ContextMenuContentProps) {
  const { open, x, y, close } = useContextMenuContext();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    function handlePointerDown(e: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        close();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open, close]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, close]);

  if (!open) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={cn(
        "fixed z-[9999] min-w-[160px] py-1",
        "rounded-lg border border-glass-stroke bg-glass-fill-dense shadow-glass backdrop-blur-md",
        className,
      )}
      style={{ left: x, top: y }}
      data-testid="context-menu-content"
      role="menu"
    >
      {children}
    </div>,
    document.body,
  );
}

/* ---------- ContextMenuItem ---------- */

interface ContextMenuItemProps {
  children: ReactNode;
  onClick?: () => void;
  destructive?: boolean;
  "data-testid"?: string;
}

export function ContextMenuItem({
  children,
  onClick,
  destructive = false,
  "data-testid": testId,
}: ContextMenuItemProps) {
  const { close } = useContextMenuContext();

  function handleClick() {
    close();
    onClick?.();
  }

  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[11px] transition-colors",
        "hover:bg-glass-fill-heavy focus:outline-none focus-visible:bg-glass-fill-heavy",
        destructive ? "text-danger-light" : "text-primary",
      )}
      onClick={handleClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

/* ---------- ContextMenuSeparator ---------- */

export function ContextMenuSeparator() {
  return (
    <div
      className="my-1 h-px bg-glass-stroke-subtle"
      role="separator"
      data-testid="context-menu-separator"
    />
  );
}
